import { useState, useEffect, createContext, useContext, useCallback, useMemo } from "react";
import { X, CheckCircle, AlertCircle, Info } from "lucide-react";

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = "info", duration = 4000) => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    
    if (duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, duration);
    }
  }, []);

  // Toast with an action button (e.g. "Rückgängig").
  // - onAction fires when the action button is clicked (and cancels the timeout)
  // - onTimeout fires when the toast auto-dismisses or is closed manually (commit)
  const addActionToast = useCallback((message, opts = {}) => {
    const { actionLabel = "Rückgängig", onAction, onTimeout, duration = 5000, type = "info" } = opts;
    const id = Date.now() + Math.random();
    let resolved = false;

    const finish = (cb) => {
      if (resolved) return;
      resolved = true;
      setToasts((prev) => prev.filter((t) => t.id !== id));
      if (cb) cb();
    };
    const commit = () => finish(onTimeout);
    const undo = () => finish(onAction);

    setToasts((prev) => [
      ...prev,
      { id, message, type, duration, action: { label: actionLabel, onClick: undo }, onDismiss: commit },
    ]);

    if (duration > 0) setTimeout(commit, duration);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useMemo(() => ({
    success: (msg) => addToast(msg, "success"),
    error: (msg) => addToast(msg, "error"),
    info: (msg) => addToast(msg, "info"),
    action: (msg, opts) => addActionToast(msg, opts),
  }), [addToast, addActionToast]);

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return context;
}

function ToastContainer({ toasts, onRemove }) {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 max-w-sm">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onRemove }) {
  const icons = {
    success: <CheckCircle className="w-5 h-5 text-green-500" />,
    error: <AlertCircle className="w-5 h-5 text-red-500" />,
    info: <Info className="w-5 h-5 text-blue-500" />,
  };

  const bgColors = {
    success: "bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800",
    error: "bg-red-50 border-red-200 dark:bg-red-950 dark:border-red-800",
    info: "bg-blue-50 border-blue-200 dark:bg-blue-950 dark:border-blue-800",
  };

  return (
    <div
      className={`relative overflow-hidden flex items-center gap-3 p-4 rounded-lg border shadow-lg ${bgColors[toast.type]} animate-in slide-in-from-right`}
    >
      {icons[toast.type]}
      <span className="flex-1 text-sm">{toast.message}</span>
      {toast.action && (
        <button
          onClick={() => toast.action.onClick()}
          className="text-sm font-semibold text-primary hover:underline shrink-0"
          data-testid="toast-undo-btn"
        >
          {toast.action.label}
        </button>
      )}
      <button
        onClick={() => (toast.onDismiss ? toast.onDismiss() : onRemove(toast.id))}
        className="text-gray-400 hover:text-gray-600"
      >
        <X className="w-4 h-4" />
      </button>
      {toast.action && toast.duration > 0 && (
        <div
          className="absolute bottom-0 left-0 h-1 bg-primary/70 rounded-full"
          style={{ animation: `toast-countdown ${toast.duration}ms linear forwards` }}
          data-testid="toast-countdown-bar"
        />
      )}
    </div>
  );
}

// Compatibility layer for sonner's toast API
let toastInstance = null;

export function setToastInstance(instance) {
  toastInstance = instance;
}

export const toast = {
  success: (msg) => toastInstance?.success(msg),
  error: (msg) => toastInstance?.error(msg),
  info: (msg) => toastInstance?.info(msg),
};
